<template>
  <div class="grid-items">
    <h3 class="page-title grid-title">
      {{ title }}
    </h3>
    <ag-grid-vue
      class="ag-theme-balham"
      data-cy="order-lines-grid"
      :grid-options="gridOptions"
      :column-defs="columnDefinitions"
      :default-col-def="defaultColDef"
      :row-data="rowData"
      :modules="modules"
      :pagination="true"
      :pagination-page-size="pageSize"
      :suppress-menu-hide="true"
      :is-full-width-row="isFullWidthRow"
      :full-width-cell-renderer="fullWidthCellRenderer"
      :get-row-height="getRowHeight"
      v-on:first-data-rendered="onFirstDataRendered"
      v-on:grid-ready="onGridReady">
    </ag-grid-vue>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
// actually using this as a string
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import GridProductTracking from './grid-product-tracking.vue';
import { GridApi, type CellClickedEvent } from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import { ordersMixins } from '@/mixins/orders.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import { OrderProduct } from '../../models/order-product.model';
import { OrderStatus, PackingSlipTrackingInformationModel } from '@onrunning/backstage-models';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { useFormat } from '../../composables/format';
import { useGrid } from '../../composables/grid';
import { useLocale } from '../../composables/locale';
import { Order } from '@/models/order.model';

export const ALL_ITEMS = 'all-items';

export default defineComponent({
  name: 'grid-order-items',
  components: {
    AgGridVue,
    // actually using this on the full width cell renderer
    // eslint-disable-next-line vue/no-unused-components
    GridProductTracking,
  },
  props: {
    order: {
      type: Order,
      required: true,
    },
    selectedShipment: {
      type: String,
      required: true,
    },
    isDraft: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const { userLocale } = useLocale();
    const { selectedAccount, selectedAccountId } = storeToRefs(useUserStore());
    const { formatCurrency } = useFormat();
    const { defaultGridOptions, dateColumnDefinition, numberColumnDefinition } = useGrid();

    return {
      selectedAccount,
      selectedAccountId,
      userLocale,
      formatCurrency,
      defaultGridOptions,
      dateColumnDefinition,
      numberColumnDefinition,
    };
  },
  data() {
    const gridOptions = this.defaultGridOptions();
    gridOptions.onPaginationChanged = this.onPaginationChanged;

    return {
      gridOptions,
      columnDefinitions: new Array<any>(),
      rowData: new Array<any>(),
      gridApi: undefined as GridApi | undefined,
      modules: [ClientSideRowModelModule],
      defaultColDef: {
        resizable: true,
        filter: true,
        sortable: true,
      },
      noRowsTemplate: `<span>${this.$t('viewOrder.shipments.no_shipments_yet')}</span>`,
      pageSize: 10,
      rowPixelsOffset: 1,
      fullWidthCellRenderer: 'GridProductTracking',
      totalRows: undefined as undefined | number,
    };
  },
  computed: {
    shipmentInformationAvailable(): boolean {
      return (
        (!this.isDraft && (this.order?.packingSlips ? this.order?.packingSlips?.length > 0 : false)) ||
        (this.order.packingSlipTrackingInfo ? this.order.packingSlipTrackingInfo?.length > 0 : false)
      );
    },
    title(): string {
      return `${this.$t('viewOrder.items').toString()} ( ${this.totalRows ?? ''} )`;
    },
  },
  watch: {
    userLocale(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.gridApi?.redrawRows();
          this.gridApi?.refreshHeader();
        });
      }
    },
  },
  mounted() {
    this.loadGrid();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    this.gridApi = undefined;
  },
  methods: {
    ...ordersMixins,
    ...utilsMixins,
    loadGrid() {
      this.rowData = [];

      if (this.showAllItems()) {
        this.setColumnDefinitionsForAllItems();
        this.setRowDataForAllItems();
      } else {
        this.setColumnDefinitionsForShipment();
        this.setRowDataForShipment();
      }
    },
    setColumnDefinitionsForAllItems() {
      this.columnDefinitions = [
        {
          headerValueGetter: () => this.$t('viewOrder.item_number'),
          field: 'lineItemNumber',
          suppressSizeToFit: true,
          width: 120,
          cellStyle: { textAlign: 'left' },
        },
        {
          headerValueGetter: () => this.$t('viewOrder.description'),
          field: 'description',
          width: 200,
          cellStyle: { textAlign: 'left' },
        },
        {
          headerValueGetter: () => this.$t('viewOrder.size'),
          field: 'size',
          width: 80,
          cellStyle: { textAlign: 'left' },
        },
      ];

      if (this.shipmentInformationAvailable) {
        this.columnDefinitions.push(
          this.numberColumnDefinition({
            headerValueGetter: () => this.$t('viewOrder.quantity_shipped'),
            width: 140,
            field: 'quantityShipped',
          }),
        );
      }

      if (!this.isDraft) {
        this.columnDefinitions.push(
          this.numberColumnDefinition({
            headerValueGetter: () => this.$t('viewOrder.quantity_cancelled'),
            width: 140,
            field: 'quantityCancelled',
          }),
        );
      }

      this.columnDefinitions.push({
        headerValueGetter: () => this.$t('viewOrder.quantity_ordered'),
        width: 140,
        field: 'quantity',
        cellStyle: { textAlign: 'left' },
        valueFormatter: (params: any) => (params.data.quantity === 0 ? '-' : params.data.quantity),
      });

      if (this.order.status === OrderStatus.UpdateInProgress) {
        this.columnDefinitions.push(
          this.numberColumnDefinition({
            headerValueGetter: () => this.$t('viewOrder.quantity_updated'),
            width: 140,
            field: 'quantityUpdated',
          }),
        );
      }

      if (this.shipmentInformationAvailable) {
        this.columnDefinitions.push({
          headerValueGetter: () => this.$t('viewOrder.tracking'),
          field: 'packingSlipTrackingInfo',
          filter: false,
          cellClass: this.getTrackerCellClass,
          width: 80,
          cellRenderer: this.trackAndTraceRenderer,
          onCellClicked: this.handleTrackingInfoClick,
        });
      }

      this.columnDefinitions.push(
        ...[
          {
            headerValueGetter: () => this.$t('viewOrder.ean'),
            width: 160,
            field: 'barcode',
            cellStyle: { textAlign: 'left' },
          },
          {
            headerValueGetter: () => this.$t('viewOrder.status'),
            field: 'lineStatus',
            width: 120,
            hide: this.isDraft,
            valueGetter: (params: any) =>
              params.data.lineStatus ? this.$t(`viewOrder.line_status.${params.data.lineStatus}`) : '-',
            cellStyle: { textAlign: 'left' },
          },
          { headerName: '', field: 'visible', hide: true, filter: 'agNumberColumnFilter' },
        ],
      );
    },
    setColumnDefinitionsForShipment() {
      this.columnDefinitions = [
        {
          headerValueGetter: () => this.$t('viewOrder.item_number'),
          field: 'lineItemNumber',
          suppressSizeToFit: true,
          width: 120,
          cellStyle: { textAlign: 'left' },
        },
        {
          headerValueGetter: () => this.$t('viewOrder.description'),
          field: 'description',
          width: 200,
          cellStyle: { textAlign: 'left' },
        },
        this.numberColumnDefinition({
          headerValueGetter: () => this.$t('viewOrder.quantity_shipped'),
          field: 'quantityShipped',
          width: 100,
        }),
        this.numberColumnDefinition({
          headerValueGetter: () => this.$t('viewOrder.quantity_ordered'),
          field: 'quantity',
          width: 100,
        }),
        this.dateColumnDefinition({
          headerValueGetter: () => this.$t('viewOrder.launch_date'),
          width: 80,
          field: 'launchDate',
        }),
        {
          headerValueGetter: () => this.$t('viewOrder.tracking'),
          field: 'trackingLink',
          width: 40,
          filter: false,
          cellStyle: { textAlign: 'center' },
          cellRenderer: this.renderTrackingButton,
          onCellClicked: this.handleTrackingLinkClick,
        },
        { headerName: '', field: 'visible', hide: true, filter: 'agNumberColumnFilter' },
      ];
    },
    handleTrackingLinkClick(params: any) {
      if (this.isURL(params.value)) {
        const newWindow = window.open(params.value, '_blank');

        newWindow?.focus();
      }
    },
    handleTrackingInfoClick(params: CellClickedEvent) {
      const slips = this.packingSlipsContainingItem(params.data.inventoryTransactionNumber);
      const trackingRow = this.rowData.find((row) => row.fullWidth && row.id === params.data.externalId + '-tracking');

      if (slips.length > 0 && trackingRow) {
        this.toggleDocumentsForRowId(params.data.externalId);
      }
    },
    toggleDocumentsForRowId(rowId: string) {
      this.rowData.forEach((item) => {
        if (item.fullWidth) {
          if (item.id === rowId + '-tracking') {
            item.visible = item.visible ? 0 : 1;
          } else {
            item.visible = 0;
          }
        }
      });

      this.gridApi?.resetRowHeights();
      this.gridApi?.onFilterChanged();
    },
    setRowDataForAllItems() {
      (this.order.products as OrderProduct[]).forEach((product) => {
        this.rowData.push({
          lineItemNumber: product.name || product.lineItemNumber,
          description: product.description,
          size: product.productSizeId || this.extractSizeFromDescription(product.description),
          unitPrice: product.unitPrice,
          quantity: product.quantity,
          quantityShipped: product.quantityShipped,
          quantityCancelled: product.quantityCancelled,
          // we hack a -1 in order not to break the ag-grid sorter which needs for all the rows to have a numeric value
          // -1 would be replaced for a "-" on rendering
          quantityUpdated: product.quantityUpdated,
          //product.quantityUpdated !== undefined ? Math.round(product.quantityUpdated) : -1,
          inventoryTransactionNumber: product.inventoryTransactionNumber,
          externalId: product.externalId,
          barcode: product.productId,
          lineStatus: product.lineStatus,
          visible: true,
        });

        if (product.inventoryTransactionNumber) {
          const packingSlips = this.packingSlipsContainingItem(product.inventoryTransactionNumber);

          if (packingSlips.length > 0) {
            this.rowData.push({
              ...product,
              packingSlips,
              id: `${product.externalId}-tracking`,
              fullWidth: true,
              visible: 0,
            });
          }
        }
      });
    },
    setRowDataForShipment() {
      const packingSlip = this.order.packingSlips?.find(
        (ps: { deliveryNumber: string }) => ps.deliveryNumber === this.selectedShipment,
      );
      const documentLines = packingSlip?.documentLines;

      if (!documentLines) {
        return;
      }

      const trackingInfo = this.order.packingSlipTrackingInfo?.find(
        (slipTracker: { packingSlipNumber: string }) => slipTracker.packingSlipNumber === this.selectedShipment,
      );

      documentLines.forEach((line: { barcode: any; quantity: number }) => {
        const product = this.order.products.find((p: any) => p.productId === line.barcode);

        if (!product) {
          return;
        }

        this.rowData.push({
          lineItemNumber: product.name || product.lineItemNumber,
          description: product.description,
          size: product.productSizeId ?? this.extractSizeFromDescription(product.description),
          unitPrice: product.unitPrice,
          quantity: Math.round(line.quantity),
          // TODO: Fins the cell name and use the formatter there
          quantityShipped: packingSlip?.documentLines?.find((l: { barcode: string }) => l.barcode === product.productId)
            ?.quantityShipped,
          inventoryTransactionNumber: product.inventoryTransactionNumber,
          externalId: product.externalId,
          lineStatus: product.lineStatus,
          launchDate: product.launchDate ? new Date(product.launchDate) : product.launchDate,
          trackingLink: trackingInfo?.trackingURL,
          visible: true,
        });
      });
    },
    trackAndTraceRenderer(params: any) {
      const invTransNr: string = params.data.inventoryTransactionNumber;

      if (invTransNr && this.packingSlipsContainingItem(invTransNr).length > 0) {
        return '<button class="ico ico-arrow-down"></button>';
      }

      return '';
    },
    currencyFormatter(params: any) {
      return this.formatCurrency(this.order.currencyCode, params.value);
    },
    showAllItems() {
      return this.selectedShipment === ALL_ITEMS;
    },
    onFirstDataRendered(params: any) {
      this.gridApi = params.api;
      this.gridApi?.sizeColumnsToFit();
      this.setFullWidthVisibilityFilter();
    },
    handleResize() {
      this.gridApi?.sizeColumnsToFit();
    },
    renderTrackingButton(params: any) {
      return this.isURL(params.value) ? '<button class="tracking-button justified-center"/>' : '';
    },
    onGridReady(grid: any) {
      this.gridApi = grid.api;
    },
    getRowHeight(params: any) {
      const isBodyRow = params.node.rowPinned === undefined;

      if (isBodyRow && params.node.data.fullWidth) {
        if (params.node.data.visible) {
          return (
            this.packingSlipsContainingItem(params.data.inventoryTransactionNumber).length *
            (this.gridOptions.rowHeight ?? 25)
          );
        }

        return -1;
      }

      return (this.gridOptions.rowHeight ?? 25) - this.rowPixelsOffset;
    },
    packingSlipsContainingItem(invTransNr: string): PackingSlipTrackingInformationModel[] {
      if (!this.order.packingSlipTrackingInfo) {
        return [];
      }

      return this.order.packingSlipTrackingInfo?.filter((packingSlip) =>
        packingSlip.trackingInformation?.find((info) => info.inventoryTransactionNumber === invTransNr),
      );
    },
    invoiceDownloadRenderer(params: any) {
      return `<button class="button download-button${params.data.loading ? ' submitting' : ''}"></button>
      <span>${this.getOrderDocumentDownloadButtonText(params.value, this.$t, this.userLocale)}</span>`;
    },
    isFullWidthRow(params: any) {
      return params.rowNode.data?.fullWidth;
    },
    setFullWidthVisibilityFilter() {
      this.gridApi?.setColumnFilterModel('visible', {
        type: 'greaterThan',
        filter: 0,
      });

      this.gridApi?.onFilterChanged();
    },
    getTrackerCellClass(params: any) {
      if (params.data.inventoryTransactionNumber) {
        if (this.packingSlipsContainingItem(params.data.inventoryTransactionNumber).length !== 0) {
          return ['clickable-cell', 'justified-center'];
        }
      }
    },
    onPaginationChanged(gridOptions: any) {
      this.totalRows = gridOptions.api?.paginationGetRowCount();
    },
  },
});
</script>
<style lang="scss" scoped>
.grid-items {
  h3 {
    font-weight: 400;
    font-size: 20px;
    text-align: left;
    margin-bottom: 46px;
  }
}
</style>
